import React, { memo, useEffect, useMemo, useCallback, useReducer, useRef, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EntityFiltersActions, ConfigActions, FuzzyActions, ServerListActions } from 'actions';
import { Filters, QuickFilters, fieldOperators, operators } from '@web/web5';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { FuzzyList } from 'utils/fuzzy';
import { logEvent } from 'utils/tracking';
import { getBackendBoolean } from 'utils/fm';
import { isEqual } from 'utils/objects';
import { translateFollowingItemForWeb, translateExtraBoolean } from 'utils/filters';
import moment from 'moment';
import { intercomHideAndShow } from 'utils/intercom';
import {
    ACTIVITIES,
    COMPANIES,
    CONTACTS,
    OPPORTUNITIES,
    SALESORDERS,
    CONVERSATIONS,
} from 'constants/Entities';
import { userflowHideAndShow } from 'utils/userflow';
import { inConversations } from '../../Activities/utils/conversations';

const {
    EMPTY,
    NOT_EMPTY,
    TODAY,
    YESTERDAY,
    THIS_WEEK,
    LAST_WEEK,
    THIS_MONTH,
    LAST_MONTH,
    THIS_QUARTER,
    LAST_QUARTER,
    THIS_YEAR,
    LAST_YEAR,
    INSIDE_RANGE,
} = operators;

const ENTITIES_WITH_ADVANCED_FILTERS = [
    ACTIVITIES.trueName,
    CONTACTS.trueName,
    COMPANIES.trueName,
    OPPORTUNITIES.trueName,
    SALESORDERS.trueName,
];

const ENTITIES_ALLOWED_OPERATORS = {
    [ACTIVITIES.trueName]: {
        date: [
            TODAY,
            YESTERDAY,
            THIS_WEEK,
            LAST_WEEK,
            THIS_MONTH,
            LAST_MONTH,
            THIS_QUARTER,
            LAST_QUARTER,
            THIS_YEAR,
            LAST_YEAR,
            INSIDE_RANGE,
        ],
    },
};

const mapStateToProps = (state, ownProps) => {
    const name = ownProps.entity ? ownProps.entity.entity : '';
    const entityFilters = state.entityFilters[ownProps.entity.entity];
    const filters = entityFilters ? entityFilters.filters : null;
    const operators = entityFilters ? entityFilters?.operators : {};
    const serverList = state.serverList;

    let principalFilter = null;
    if (
        state.config.userData &&
        state.config.userData.principalFilter &&
        state.config.userData.principalFilter[ownProps.entity.entity]
    ) {
        principalFilter = state.config.userData.principalFilter[ownProps.entity.entity];
    }

    const license = state.config?.subscriptionModeCode || '';

    return {
        filters,
        serverList,
        principalFilter,
        cache: state.config.components[`${name}_filters`],
        views: state.config.views,
        viewsPublicManagement: state.config.permission.viewsPublicManagement,
        viewsPrivateManagement: state.config.permission.viewsPrivateManagement,
        standardFieldsSchemaMap:
            state.config?.standardFieldsSchemaMap?.[ownProps?.entity?.extraFieldName] || null,
        filterDragging: entityFilters?.filterDragging,
        operators,
        license,
        enableSalesOrdersTypes: state.config?.userData?.enableSalesOrdersTypes,
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    let name = ownProps.entity ? ownProps.entity.entity : '';
    const setConfigWeb = bindActionCreators(ConfigActions, dispatch).setConfigWeb;

    return {
        initFilters: bindActionCreators(EntityFiltersActions, dispatch).initFilters,
        toggleFilters: bindActionCreators(EntityFiltersActions, dispatch).toggleFilters,
        changeFilter: bindActionCreators(EntityFiltersActions, dispatch).changeFilter,
        changeAdvancedFilter: bindActionCreators(EntityFiltersActions, dispatch)
            .changeAdvancedFilter,
        deleteMultipleFilters: bindActionCreators(EntityFiltersActions, dispatch)
            .deleteMultipleFilters,
        clearFilters: bindActionCreators(EntityFiltersActions, dispatch).clearFilters,
        saveCache: (config) => setConfigWeb(`${name}_filters`, config),
        setConfigWeb,
        getFuzzy: bindActionCreators(FuzzyActions, dispatch).getFuzzy,
        getList: bindActionCreators(ServerListActions, dispatch).getList,
        removeView: bindActionCreators(EntityFiltersActions, dispatch).removeView,
        createView: bindActionCreators(EntityFiltersActions, dispatch).createView,
        setFilterDragging: bindActionCreators(EntityFiltersActions, dispatch).setFilterDragging,
    };
};

const initialState = {
    schema: [],
    visibleFilters: [],
    openFilters: {},
    extraSchema: [],
    updatedDefaultFilters: [],
    quickFilters: [],
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'init':
            return { ...state, ...action.payload };
        case 'openFilters':
            return { ...state, openFilters: action.payload };
        case 'visibleFilters':
            return { ...state, visibleFilters: action.payload };
        case 'quickFilters':
            return { ...state, quickFilters: action.payload };
        case 'saveConfig': {
            return {
                ...state,
                ...action.payload,
            };
        }
    }
};

const NewEntityFilters = memo(
    ({
        className,
        useQuickFilters,
        useQuickView,
        useViewsInFilters,
        useViewsInMenu,
        entity,
        isOpen,
        defaultQuickFilters = [],
        defaultFilters = [],
        hiddenFilters = [],
        cache,
        filters,
        serverList,
        principalFilter,
        initFilters,
        changeFilter,
        deleteMultipleFilters,
        clearFilters,
        toggleFilters,
        filtersAddButtonSchema = [],
        saveCache,
        getFuzzy,
        getList,
        views,
        createView,
        removeView,
        useDrawer,
        dontIgnoreTheseFilters,
        viewsPublicManagement,
        viewsPrivateManagement,
        standardFieldsSchemaMap,
        getCanClearAll,
        clearSpecificFilter,
        setConfigWeb,
        filterDragging,
        setFilterDragging,
        operators,
        changeAdvancedFilter,
        license,
        enableSalesOrdersTypes,
        ...props
    }) => {
        const [state, dispatch] = useReducer(reducer, initialState);
        const isFirstLoad = useRef(true);
        const debounce = useRef(null);
        const cachedOptions = useRef({});
        const quickFilterRef = useRef(cache?.quickFilters || []);
        const location = useLocation();

        const hasAdvancedFilters = useMemo(
            () => ENTITIES_WITH_ADVANCED_FILTERS.includes(entity.trueName),
            [entity],
        );

        const allowedOperators = useMemo(
            () => ENTITIES_ALLOWED_OPERATORS[entity.trueName],
            [entity],
        );

        useEffect(() => {
            // Sync quick filters
            if (!isEqual(cache?.quickFilters, quickFilterRef.current) && useQuickFilters) {
                dispatch({ type: 'quickFilters', payload: cache?.quickFilters || [] });
            }
            quickFilterRef.current = cache?.quickFilters;
        }, [cache?.quickFilters, useQuickFilters]);

        const addFixedFilters = useCallback(
            (quickFilters) => {
                // --------------------------------------------------------------------------------------
                // GOKU-5779:
                // Temporary fix to force the "Pending Conversations" filter into activities quickfilters
                // --------------------------------------------------------------------------------------
                if (
                    !quickFilters.includes('pendingConversations') &&
                    entity.trueName === ACTIVITIES.trueName
                )
                    quickFilters.push('pendingConversations');

                if (
                    !quickFilters.includes('isMarketingEmail') &&
                    entity.trueName === ACTIVITIES.trueName
                )
                    quickFilters.push('isMarketingEmail');

                if (!quickFilters.includes('state') && entity.trueName === OPPORTUNITIES.trueName) {
                    quickFilters.unshift('state');
                }
            },
            [entity],
        );

        const getFixedVisibleFilters = useCallback(() => {
            let visible = [];
            if (entity.trueName === ACTIVITIES.trueName) {
                visible.push('isMarketingEmail');
            }

            return visible;
        }, [entity?.trueName]);

        const getStateCached = useCallback(() => {
            let cachedState = null;
            let visibleFilters = [];
            let quickFilters = [];
            let openFilters = {};

            if (cache) {
                quickFilters = cache.hasOwnProperty('quickFilters')
                    ? cache.quickFilters || []
                    : defaultQuickFilters;

                addFixedFilters(quickFilters);

                // All quick filters have to be visible for consistency
                visibleFilters = new Set([
                    ...(cache.visibleFilters || []),
                    ...quickFilters,
                    ...getFixedVisibleFilters(),
                ]);

                visibleFilters = Array.from(visibleFilters);
                visibleFilters = visibleFilters.filter(
                    (filter) => typeof filter === 'string' || filter instanceof String,
                );

                openFilters = cache.openFilters;

                cachedState = {
                    visibleFilters,
                    openFilters,
                    quickFilters,
                };
            } else {
                cachedState = {
                    visibleFilters: defaultFilters,
                    openFilters,
                    quickFilters: defaultQuickFilters,
                };
            }

            return cachedState;
        }, [cache, defaultQuickFilters, addFixedFilters, getFixedVisibleFilters, defaultFilters]);

        const getMappedSchema = useCallback((schema) => {
            return schema.reduce((obj, current) => {
                if (!obj.schemaByFieldId) obj.schemaByFieldId = {};
                if (!obj.schemaByFieldConfiguration) obj.schemaByFieldConfiguration = {};
                obj.schemaByFieldId[current.id] = current;
                if (current.fieldConfiguration) {
                    obj.schemaByFieldConfiguration[current.fieldConfiguration] = current;
                }
                return obj;
            }, {});
        }, []);

        const filtersToIgnore = useMemo(() => {
            let newFiltersToIgnore = ['entityShowSelected', 'onlySelected', 'idProductFamily'];
            if (enableSalesOrdersTypes) newFiltersToIgnore.push('salesOrderType');
            if (dontIgnoreTheseFilters?.length > 0) {
                newFiltersToIgnore = newFiltersToIgnore.filter(
                    (current) => !dontIgnoreTheseFilters.includes(current),
                );
            }
            return newFiltersToIgnore;
        }, [dontIgnoreTheseFilters, enableSalesOrdersTypes]);

        const newDefaultFilters = useMemo(() => {
            const filters = state.updatedDefaultFilters.filter(
                (current) => !filtersToIgnore.includes(current),
            );
            return filters;
        }, [filtersToIgnore, state.updatedDefaultFilters]);

        const init = useCallback(() => {
            initFilters(entity).then(([schema, extraFieldsTab]) => {
                const cachedState = getStateCached();
                const { schemaByFieldId } = getMappedSchema(schema);

                let cachedVisible = cachedState?.visibleFilters;
                let finalFilters = cachedVisible ? [...cachedVisible] : [...defaultFilters];

                finalFilters = finalFilters.filter((current) => {
                    return !filtersToIgnore.includes(current);
                });

                // Adding extra field groups
                const groupedExtraFields = extraFieldsTab.reduce((groups, tab) => {
                    groups.push({
                        label: tab.title,
                        options: tab.fields.map((field) => field.id),
                    });
                    return groups;
                }, []);

                dispatch({
                    type: 'init',
                    payload: {
                        ...cachedState,
                        schema,
                        mappedSchema: schemaByFieldId,
                        visibleFilters: finalFilters,
                        extraSchema: [...(filtersAddButtonSchema || []), ...groupedExtraFields],
                        updatedDefaultFilters: [...defaultFilters],
                    },
                });
            });
        }, [
            entity,
            initFilters,
            getStateCached,
            getMappedSchema,
            filtersAddButtonSchema,
            defaultFilters,
            filtersToIgnore,
        ]);

        useEffect(() => {
            if (isFirstLoad.current) {
                init();
                isFirstLoad.current = false;
            }
        }, [init]);

        const extraFiltersSchema = useMemo(() => {
            const options = state.schema.reduce((filters, filter) => {
                let isStaticFilter = false;

                if (
                    filter.id === 'pendingConversations' &&
                    entity.trueName === ACTIVITIES.trueName
                ) {
                    isStaticFilter = true;
                } else if (filter.id === 'state' && entity.trueName === OPPORTUNITIES.trueName) {
                    isStaticFilter = true;
                }

                if (!hiddenFilters.includes(filter.id)) {
                    filters.push({
                        id: filter.id,
                        label: filter.description,
                        forceDisabled: isStaticFilter,
                    });
                }
                return filters;
            }, []);

            let groupedOptions = [];

            if (state.extraSchema && state.extraSchema.length > 0) {
                const mappedOptions = options.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {});

                groupedOptions = state.extraSchema.reduce((result, current) => {
                    let group = {
                        label: current.label,
                        id: current.label,
                    };

                    let options = current.options.reduce((arr, option) => {
                        if (mappedOptions[option]) {
                            arr.push(mappedOptions[option]);
                            mappedOptions[option].included = true;
                        }
                        return arr;
                    }, []);

                    group.options = options;
                    result.push(group);

                    return result;
                }, []);
            }

            let finalOptions = options;
            if (groupedOptions && groupedOptions.length > 0) finalOptions = groupedOptions;

            return finalOptions;
        }, [state.schema, state.extraSchema, hiddenFilters, entity.trueName]);

        const saveNewCache = useCallback(
            (newCache) => {
                const compareFields = ['visibleFilters', 'openFilters', 'quickFilters'];
                return saveCache(
                    compareFields.reduce((obj, field) => {
                        obj[field] = newCache[field] || state[field];
                        return obj;
                    }, {}),
                );
            },
            [state, saveCache],
        );

        const formatProbabilityToShow = useCallback((newValue) => {
            newValue = [newValue.from * 10, newValue.to * 10];
            return newValue;
        }, []);

        const formatProbabilityToSave = useCallback((newValue) => {
            newValue = { from: newValue?.from / 10 || 0, to: newValue?.to / 10 || 0 };
            return newValue;
        }, []);

        const rangeFields = useMemo(
            () => ['date', 'integer', 'decimal', 'currency', 'percent', 'probability'],
            [],
        );

        const formatRangeValueToShow = useCallback(
            (value, field) => {
                if (!value) return [];
                if (Array.isArray(value)) return value;
                let formattedValue;
                if (field.dataType === 'probability') {
                    formattedValue = formatProbabilityToShow(value);
                } else if (field.dataType === 'date') {
                    const from = value['from'];
                    const to = value['to'];
                    formattedValue = [
                        from ? moment.utc(from).local().toDate() : '',
                        to ? moment.utc(to).local().toDate() : '',
                    ];
                } else {
                    formattedValue = [value['from'], value['to']];
                }
                return formattedValue;
            },
            [formatProbabilityToShow],
        );

        const formatRangeValueToSave = useCallback(
            (value, field) => {
                let defaultFrom = '';
                let defaultTo = '';
                if (field?.inputProps?.min || !isNaN(field?.inputProps?.min)) {
                    defaultFrom = field.inputProps.min;
                }
                if (field?.inputProps?.max || !isNaN(field?.inputProps?.max)) {
                    defaultTo = field.inputProps.max;
                }
                let formattedValue = {
                    from: value?.[0] || defaultFrom,
                    to: value?.[1] || defaultTo,
                };
                if (field.dataType === 'probability') {
                    formattedValue = formatProbabilityToSave(formattedValue);
                }
                return formattedValue;
            },
            [formatProbabilityToSave],
        );

        const relatedEntity = useMemo(() => {
            switch (true) {
                case inConversations(location):
                    return CONVERSATIONS;
                default:
                    return null;
            }
        }, [location]);

        const onChangeFilter = useCallback(
            (filter, value, info) => {
                let completeValues = null;
                let newValue = null;
                const hasOperator = hasAdvancedFilters && operators && !!operators?.[filter.id];
                if (rangeFields.includes(filter.dataType)) {
                    value = formatRangeValueToSave(value, filter);
                }
                if (value && Array.isArray(value)) {
                    completeValues = value.map((current) => {
                        if (!newValue) newValue = [];
                        newValue.push(current.value || current);
                        return {
                            value: current.value || current,
                            label: current.label,
                            idParent: current.idParent || current.idparent,
                        };
                    });
                } else {
                    newValue = value?.value || value;
                    completeValues = value;
                }

                if (filter.customFormatValueToSave) {
                    newValue = filter.customFormatValueToSave(newValue);
                }

                if (filter.customFormatCompleteValuesToSave) {
                    completeValues = filter.customFormatCompleteValuesToSave(
                        completeValues,
                        filter,
                    );
                }

                if (useQuickFilters) {
                    logEvent({ event: entity.trueName, functionality: 'quickFilter' });
                    hasOperator &&
                        logEvent({ event: entity.trueName, functionality: 'advancedFilters' });
                } else {
                    if (newValue && newValue?.length) {
                        logEvent({ event: entity.trueName, functionality: 'filter' });
                        hasOperator &&
                            logEvent({ event: entity.trueName, functionality: 'advancedFilters' });
                    }
                }

                const inputAttrs = filter.inputAttrs || null;

                if (
                    (!newValue ||
                        (inputAttrs?.minSelection && inputAttrs?.minSelection > newValue.length)) &&
                    inputAttrs?.backupSelection
                ) {
                    newValue = inputAttrs.backupSelection?.value || null;
                    completeValues = inputAttrs.backupSelection?.completeValues || null;
                }

                changeFilter(
                    entity,
                    filter,
                    newValue,
                    undefined,
                    completeValues,
                    null,
                    info,
                    false,
                    relatedEntity,
                );
            },
            [
                hasAdvancedFilters,
                operators,
                rangeFields,
                useQuickFilters,
                changeFilter,
                entity,
                formatRangeValueToSave,
                relatedEntity,
            ],
        );

        const onClearFilters = useCallback(() => {
            if (filters.customView) {
                sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
            }

            if (clearSpecificFilter) {
                clearSpecificFilter();
            }

            if (useQuickFilters) {
                logEvent({ event: entity.trueName, functionality: 'quickClearFilter' });
            } else {
                logEvent({ event: entity.trueName, functionality: 'clearFilter' });
            }
            clearFilters(entity, false, true, true, true, relatedEntity);
            changeAdvancedFilter(entity, {});
        }, [
            filters,
            clearSpecificFilter,
            useQuickFilters,
            clearFilters,
            entity,
            changeAdvancedFilter,
            relatedEntity,
        ]);

        const onResetVisibles = useCallback(() => {
            dispatch({ type: 'quickFilters', payload: defaultQuickFilters });

            saveNewCache({
                quickFilters: defaultQuickFilters,
            });

            logEvent({
                event: entity.trueName,
                functionality: 'resetFilterSelection',
            });
        }, [entity, defaultQuickFilters, saveNewCache]);

        const onAddFilter = useCallback(
            (filters) => {
                const newVisibleFilters = [...filters];
                const newQuickFilters = state.quickFilters.filter((filter) =>
                    filters.includes(filter),
                );
                dispatch({ type: 'visibleFilters', payload: newVisibleFilters });
                dispatch({ type: 'quickFilters', payload: newQuickFilters });
                saveNewCache({
                    visibleFilters: newVisibleFilters,
                    quickFilters: newQuickFilters,
                });
            },
            [saveNewCache, state.quickFilters],
        );

        const onDeleteFilter = useCallback(
            (filter) => {
                const filterObj = filters[filter];
                const newVisibleFilters = state.visibleFilters.filter((f) => f !== filter);
                const newQuickFilters = state.quickFilters.filter((f) => f !== filter);
                const newOpenFilters = Object.keys(state.openFilters).reduce((obj, key) => {
                    if (key === filter) return obj;
                    obj[key] = state.openFilters[key];
                    return obj;
                }, {});

                logEvent({ event: entity.trueName, functionality: 'deleteFilter' });
                const config = {
                    openFilters: newOpenFilters,
                    visibleFilters: newVisibleFilters,
                    quickFilters: newQuickFilters,
                };
                dispatch({ type: 'saveConfig', payload: config });
                saveNewCache(config).finally(() => {
                    if (filterObj) onChangeFilter(filterObj, null);
                    // Delete advanced filter if it's set & only update entity if !filterObj
                    if (operators && operators[filter]) {
                        const { [filter]: undefined, ...operatorsRest } = operators;
                        changeAdvancedFilter(entity, operatorsRest, !filterObj);
                    }
                });
            },
            [
                filters,
                state.visibleFilters,
                state.quickFilters,
                state.openFilters,
                entity,
                saveNewCache,
                operators,
                onChangeFilter,
                changeAdvancedFilter,
            ],
        );

        const onDeleteMultipleFilters = useCallback(
            (filters) => {
                deleteMultipleFilters(entity, filters);
            },
            [entity, deleteMultipleFilters],
        );

        const onToggleFilter = useCallback(
            (filter) => {
                const isToggled = state.openFilters[filter] ? false : true;
                const newOpenFilters = { [filter]: isToggled };
                dispatch({
                    type: 'openFilters',
                    payload: newOpenFilters,
                });
                saveNewCache({
                    openFilters: newOpenFilters,
                });
            },
            [state.openFilters, dispatch, saveNewCache],
        );

        const formatViews = useMemo(() => {
            const viewsName = entity.views;
            if (!views?.length > 0) return [];
            return views.reduce((obj, view) => {
                if (view.entity === viewsName) {
                    obj.push({
                        value: parseInt(view.id, 10),
                        label: view.name,
                        item: view,
                    });
                }
                return obj;
            }, []);
        }, [views, entity]);

        const formatCustomView = useCallback((customView) => {
            if (!customView) return null;
            return {
                value: {
                    value: customView.SelectFilterId,
                    label: customView.label,
                    item: {
                        id: customView.SelectFilterId,
                        isPublic: false,
                    },
                },
                label: customView.label,
                id: 'customView',
                hideForCount: false,
            };
        }, []);

        const onChangeView = useCallback(
            (view) => {
                onChangeFilter(
                    {
                        id: 'idView',
                        dataType: 'view',
                        hideForCount: false,
                    },
                    view,
                );
                logEvent({ event: entity.trueName, functionality: 'view' });
            },
            [onChangeFilter, entity],
        );

        const onChangeCustomView = useCallback(
            (view) => {
                sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
                onDeleteFilter(filters.customView.id);
                onChangeFilter(
                    {
                        id: 'idView',
                        dataType: 'view',
                        hideForCount: false,
                    },
                    view,
                );
            },
            [filters, onChangeFilter, onDeleteFilter],
        );

        const onDeleteCustomView = useCallback(() => {
            sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
            onDeleteFilter(filters.customView.id);
            return Promise.resolve();
        }, [filters, onDeleteFilter]);

        const onSaveView = useCallback(
            ({ name, isPrivate }) => {
                return new Promise((resolve, reject) => {
                    const isPublic = !isPrivate;
                    createView(entity, {
                        name,
                        isPublic,
                    })
                        .then((view) => {
                            clearFilters(entity, false, false);
                            changeAdvancedFilter(entity, {});

                            onChangeView({
                                value: view.id,
                                label: view.name,
                                item: view,
                            });
                            successToast({ text: getLiteral('label_view_successfully_created') });
                            resolve();
                        })
                        .catch((err) => {
                            console.error(err);
                            errorToast({ text: getLiteral('error_generalerror') });
                            reject();
                        });
                });
            },
            [createView, entity, clearFilters, changeAdvancedFilter, onChangeView],
        );

        const onDeleteView = useCallback(
            (view) => {
                return new Promise((resolve, reject) => {
                    removeView(view.item)
                        .then(() => {
                            successToast({ text: getLiteral('label_view_successfully_deleted') });
                            resolve();
                        })
                        .catch((err) => {
                            console.error(err);
                            errorToast({ text: getLiteral('error_generalerror') });
                            reject();
                        });

                    onChangeView(null);
                });
            },
            [removeView, onChangeView],
        );

        const isFuzzy = useCallback((list, field) => {
            if (!list) return false;
            return (
                FuzzyList.includes(list.toLowerCase()) ||
                getBackendBoolean(field.fuzzySearchValueList)
            );
        }, []);

        const fetchOptions = useCallback(
            ({ text, list, field, defaultSearch }) => {
                return getFuzzy({ field, list, text, feature: entity.entity });
            },
            [getFuzzy, entity],
        );

        const loadFuzzyOptions = useCallback(
            (field) => {
                return (text) => {
                    return new Promise((resolve) => {
                        if (!field.inputAttrs) return resolve([]);
                        const cachedOptionsKey = `${field.inputAttrs.list}__${text}`;
                        if (cachedOptions.current[cachedOptionsKey])
                            return resolve(cachedOptions.current[cachedOptionsKey]);
                        clearTimeout(debounce.current);
                        debounce.current = setTimeout(() => {
                            fetchOptions({ text, ...field.inputAttrs })
                                .then((options) => {
                                    let newOptions = options;
                                    if (field.beforeRenderValueList) {
                                        newOptions = field.beforeRenderValueList(
                                            newOptions,
                                            field?.inputAttrs?.list,
                                        );
                                    }
                                    if (field.groupFunction) {
                                        newOptions = field.groupFunction(newOptions);
                                    }
                                    cachedOptions.current[text] = newOptions;
                                    resolve(newOptions);
                                })
                                .catch(console.error);
                        }, 500);
                    });
                };
            },
            [fetchOptions],
        );

        const loadOptions = useCallback(
            (field) => () => {
                if (!field?.inputAttrs?.list) return Promise.resolve([]);
                return new Promise((resolve, reject) => {
                    getList(field.inputAttrs.list)
                        .then((options) => {
                            let newOptions = options;
                            if (field.beforeRenderValueList) {
                                newOptions = field.beforeRenderValueList(
                                    newOptions,
                                    field?.inputAttrs?.list,
                                );
                            }
                            if (field.groupFunction) {
                                newOptions = field.groupFunction(newOptions);
                            }
                            resolve(newOptions);
                        })
                        .catch(reject);
                });
            },
            [getList],
        );

        const getFieldFormatted = useCallback(
            (field) => {
                const newField = { ...field };
                if (!newField.inputProps) newField.inputProps = {};
                if (field.hint) newField.inputProps.placeholder = field.hint;
                switch (field.dataType) {
                    case 'bool':
                        newField.inputProps.options = newField?.inputAttrs?.options || [
                            { label: getLiteral('common_yes'), value: '1' },
                            { label: getLiteral('common_no'), value: '2' },
                        ];
                        break;
                    case 'probability':
                        newField.inputProps = {
                            min: 0,
                            max: 100,
                        };
                        break;
                    case 'singleValueList':
                    case 'relatedValueList':
                    case 'multipleValueList':
                        if (field?.generateOptions) {
                            newField.inputProps.options = field.generateOptions() || [];
                        } else if (
                            field?.inputAttrs?.list &&
                            isFuzzy(field?.inputAttrs?.list, field)
                        ) {
                            newField.inputProps.isFuzzy = true;
                            newField.inputProps.isMulti = newField.inputAttrs.hasOwnProperty(
                                'isMulti',
                            )
                                ? newField.inputAttrs.isMulti
                                : true;
                            newField.inputProps.loadOptions = loadFuzzyOptions(field);
                            newField.inputProps.defaultOptions = true;
                            newField.inputProps.defaultSearch =
                                field.inputAttrs?.defaultSearch || '';
                        } else {
                            newField.inputProps.isMulti = true;
                            if (field?.inputAttrs?.list) {
                                newField.inputProps.loadOptions = loadOptions(field);
                            }
                        }

                        if (field?.inputAttrs?.filterOption) {
                            newField.inputProps.overrides = {
                                'react-select': {
                                    filterOption: field?.inputAttrs?.filterOption,
                                },
                            };
                        }

                        if (!newField.inputProps.placeholder) {
                            newField.inputProps.placeholder = getLiteral('label_selectone');
                        }

                        if (newField.inputProps.isMulti && !newField.inputProps.isFuzzy) {
                            newField.inputProps.selectAllLabel = getLiteral('label_selectall');
                        }

                        // Filtering options if dependent of principal filter
                        if (
                            standardFieldsSchemaMap?.[newField.fieldConfiguration]
                                ?.strParentField === principalFilter
                        ) {
                            newField.inputProps.overrides = {
                                'react-select': {
                                    filterOption: ({ data }) => {
                                        if (!filters || !data) return true;
                                        let principal = Object.keys(filters).find(
                                            (filterKey) =>
                                                filters[filterKey]?.fieldConfiguration ===
                                                principalFilter,

                                            {},
                                        );
                                        if (principal) principal = filters[principal]?.value?.[0];

                                        return (
                                            (principal && principal === data?.idparent) ||
                                            !principal
                                        );
                                    },
                                },
                            };
                        }

                        if (field.inputAttrs?.overrides) {
                            if (newField.inputProps.overrides) {
                                newField.inputProps.overrides = {
                                    ...newField.inputProps.overrides,
                                    ...field.inputAttrs.overrides,
                                    'react-select': {
                                        ...newField.inputProps.overrides['react-select'],
                                        ...(field.inputAttrs.overrides?.['react-select'] || {}),
                                    },
                                };
                            } else {
                                newField.inputProps.overrides = {
                                    ...field.inputAttrs.overrides,
                                };
                            }
                        }

                        if (field.inputAttrs?.className) {
                            newField.inputProps.className = field.inputAttrs.className;
                        }

                        if (field.inputAttrs?.getClassName) {
                            newField.inputProps.className = field.inputAttrs.getClassName();
                        }

                        break;
                    case 'text':
                        if (!newField?.inputAttrs?.placeholder) {
                            newField.inputProps.placeholder = getLiteral('placeholder_search');
                        }
                }

                // Set Quickfilters visual hint value if advanced filter is set
                if (operators && operators[field.id]) newField.operator = operators[field.id];

                return {
                    ...newField,
                };
            },
            [
                isFuzzy,
                standardFieldsSchemaMap,
                principalFilter,
                loadFuzzyOptions,
                loadOptions,
                filters,
                operators,
            ],
        );

        const getFieldsFormatted = useCallback(
            (fields) => {
                const formattedField = getFieldFormatted(fields);
                return [formattedField];
            },
            [getFieldFormatted],
        );

        const toggleQuickFilter = useCallback(
            (filter) => {
                const alreadyExists = state.quickFilters.includes(filter);
                const newQuickFilters = alreadyExists
                    ? state.quickFilters.filter((i) => i !== filter)
                    : [...state.quickFilters, filter];
                dispatch({ type: 'quickFilters', payload: newQuickFilters });
                saveNewCache({
                    quickFilters: newQuickFilters,
                });

                logEvent({
                    event: entity.trueName,
                    functionality: alreadyExists ? 'unpinQuickFilter' : 'pinQuickFilter',
                });
            },
            [entity.trueName, saveNewCache, state.quickFilters],
        );

        const onReOrder = useCallback(
            (startIndex, endIndex) => {
                const result = [...state.quickFilters];
                const [removed] = result.splice(startIndex, 1);
                result.splice(endIndex, 0, removed);
                dispatch({ type: 'quickFilters', payload: result });
                saveNewCache({
                    quickFilters: result,
                });

                logEvent({
                    event: entity.trueName,
                    functionality: 'orderQuickFilter',
                });
            },
            [entity.trueName, saveNewCache, state.quickFilters],
        );

        const onClose = useCallback(() => {
            toggleFilters(entity, false);
            intercomHideAndShow();
            userflowHideAndShow();
        }, [entity, toggleFilters]);

        const onDrag = useCallback(
            (item) => {
                setFilterDragging(entity, item.draggableId);
            },
            [entity, setFilterDragging],
        );

        const onDrop = useCallback(
            (item) => {
                // Keep state for eye tracking (ux)
                setTimeout(() => {
                    setFilterDragging(entity, null);
                }, 750);
            },
            [entity, setFilterDragging],
        );

        const formatFinalSchema = useMemo(() => {
            if (!state.visibleFilters || state.visibleFilters.length === 0) return [];
            return state.visibleFilters
                .filter((filter) => !state.quickFilters.includes(filter))
                .reduce((arr, current) => {
                    if (!state.mappedSchema[current]) return arr;
                    if (hiddenFilters.includes(current)) return arr;
                    // Use this if we want to allow removing the filter or not according if it is default or not.
                    // const isDefault = newDefaultFilters?.includes(current) || false;

                    let isRemovable = true;
                    if (state.mappedSchema[current]?.hasOwnProperty('isRemovable')) {
                        isRemovable = state.mappedSchema[current].isRemovable;
                    }

                    let isPinnable = true;
                    if (state.mappedSchema[current]?.hasOwnProperty('isPinnable')) {
                        isPinnable = state.mappedSchema[current].isPinnable;
                    }

                    arr.push({
                        label: state.mappedSchema[current].description,
                        id: state.mappedSchema[current].id,
                        fields: [...getFieldsFormatted(state.mappedSchema[current])],
                        isRemovable,
                        isPinnable,
                    });

                    return arr;
                }, []);
        }, [
            state.visibleFilters,
            state.quickFilters,
            state.mappedSchema,
            hiddenFilters,
            getFieldsFormatted,
        ]);

        const formatFinalQuickSchema = useMemo(() => {
            if (!state.quickFilters || state.quickFilters.length === 0) return [];
            return state.quickFilters.reduce((arr, current) => {
                if (!state.mappedSchema?.[current]) return arr;
                if (hiddenFilters.includes(current)) return arr;

                let isRemovable = true;
                if (state.mappedSchema[current]?.hasOwnProperty('isRemovable')) {
                    isRemovable = state.mappedSchema[current].isRemovable;
                }

                let isPinnable = true;
                if (state.mappedSchema[current]?.hasOwnProperty('isPinnable')) {
                    isPinnable = state.mappedSchema[current].isPinnable;
                }

                arr.push({
                    label: state.mappedSchema[current].description,
                    id: state.mappedSchema[current].id,
                    fields: [...getFieldsFormatted(state.mappedSchema[current])],
                    isRemovable,
                    isPinnable,
                });
                return arr;
            }, []);
        }, [state.quickFilters, state.mappedSchema, hiddenFilters, getFieldsFormatted]);

        const values = useMemo(() => {
            if (!state.mappedSchema) return {};
            if (!filters || filters.length === 0) return {};

            let newFilters = translateFollowingItemForWeb(filters, entity);

            const newValues = Object.keys(newFilters).reduce((obj, key) => {
                if (filtersToIgnore.includes(key)) return obj;

                let newFilter = filters[key];

                newFilter = translateExtraBoolean(newFilter, entity);

                let value = newFilter.completeValues || newFilter.value;
                if (rangeFields.includes(newFilter.dataType)) {
                    const field = state.mappedSchema[key] || null;
                    value = formatRangeValueToShow(value, field);
                }

                obj[key] = value;
                return obj;
            }, {});

            return newValues;
        }, [
            filters,
            filtersToIgnore,
            state.mappedSchema,
            rangeFields,
            formatRangeValueToShow,
            entity,
        ]);

        const useViews = useMemo(() => {
            if (!useViewsInFilters && !useViewsInMenu) return false;
            return true;
        }, [useViewsInFilters, useViewsInMenu]);

        const hasFiltersActive = useMemo(() => {
            if (!filters) return false;
            let { entityShowSelected, matchingName, idProductFamily, ...otherFilters } = filters;
            if (enableSalesOrdersTypes) {
                // Removing salesOrderType
                otherFilters = Object.keys(otherFilters).reduce((obj, filterKey) => {
                    if (filterKey === 'salesOrderType') return obj;
                    obj[filterKey] = otherFilters[filterKey];
                    return obj;
                }, {});
            }
            return Object.entries(otherFilters).length > 0;
        }, [enableSalesOrdersTypes, filters]);

        const onChangeOperator = useCallback(
            (filter, value) => {
                let newOperators;
                const { id, dataType, isExtra, asExtra, serverKeys } = filter;
                const extra = asExtra || isExtra;
                const isDefaultValue = fieldOperators[dataType].default === value.value;
                const shouldUpdateEntity =
                    !!values[id] || [EMPTY, NOT_EMPTY].includes(value.value) || !!operators?.[id];

                if (isDefaultValue) {
                    const { [id]: undefined, ...restOfOperators } = operators || {};
                    newOperators = restOfOperators;
                } else {
                    newOperators = {
                        ...(operators || {}),
                        [id]: {
                            ...value,
                            extra: !!extra,
                            serverKeys,
                            dataType,
                        },
                    };

                    if (filter.dataType === 'date') {
                        let functionalityName = value.value;
                        if (functionalityName) {
                            functionalityName = `${functionalityName
                                .charAt(0)
                                .toLowerCase()}${functionalityName.slice(1)}`;
                        }
                        logEvent({
                            event: entity.trueName,
                            submodule: 'filterByDateField',
                            functionality: functionalityName,
                        });
                    }

                    if (shouldUpdateEntity && !useQuickFilters) {
                        logEvent({
                            event: entity.trueName,
                            functionality: useQuickFilters ? 'quickFilter' : 'filter',
                        });
                        logEvent({ event: entity.trueName, functionality: 'advancedFilters' });
                    }
                }

                changeAdvancedFilter(entity, newOperators || {}, shouldUpdateEntity);
            },
            [values, operators, changeAdvancedFilter, entity, useQuickFilters],
        );

        return (
            <Fragment>
                {useQuickFilters && (
                    <QuickFilters
                        className={className}
                        useQuickView={useQuickView}
                        filters={state.quickFilters}
                        schema={formatFinalQuickSchema}
                        onChange={onChangeFilter}
                        onClearAll={onClearFilters}
                        values={values}
                        views={formatViews}
                        selectedView={values?.customView || values?.idView || null}
                        customView={formatCustomView(values?.customView || null)}
                        onChangeView={onChangeView}
                        onChangeCustomView={onChangeCustomView}
                        hasFiltersActive={hasFiltersActive}
                        resetFilter={() => init(true)}
                        getCanClearAll={getCanClearAll}
                        filterDragging={filterDragging}
                        advancedFilters={hasAdvancedFilters}
                        operators={operators}
                        onChangeOperator={onChangeOperator}
                        allowedOperators={allowedOperators}
                        license={license}
                    />
                )}
                {!useQuickFilters && (
                    <Filters
                        quickFilters={state.quickFilters}
                        toggleQuickFilter={toggleQuickFilter}
                        onReOrder={onReOrder}
                        defaultFilters={newDefaultFilters}
                        schema={formatFinalSchema}
                        quickSchema={formatFinalQuickSchema}
                        extraSchema={extraFiltersSchema}
                        showAdd={true}
                        isOpen={isOpen}
                        toggled={state.openFilters}
                        visibleFilters={state.visibleFilters}
                        onChange={onChangeFilter}
                        onToggle={onToggleFilter}
                        onConfirmAdd={onAddFilter}
                        onDelete={onDeleteFilter}
                        onDeleteMultiple={onDeleteMultipleFilters}
                        onClearAll={onClearFilters}
                        onClose={onClose}
                        values={values}
                        useViewsInFilters={useViewsInFilters}
                        useViewsInMenu={useViewsInMenu}
                        views={formatViews}
                        selectedView={values?.customView || values?.idView || null}
                        customView={formatCustomView(values?.customView || null)}
                        onChangeView={onChangeView}
                        onChangeCustomView={onChangeCustomView}
                        onSaveView={useViews && onSaveView ? onSaveView : undefined}
                        onDeleteView={onDeleteView}
                        onDeleteCustomView={onDeleteCustomView}
                        canManagePublicViews={viewsPublicManagement}
                        canManagePrivateViews={viewsPrivateManagement}
                        useDrawer={useDrawer}
                        onResetVisibles={onResetVisibles}
                        onDrag={onDrag}
                        onDrop={onDrop}
                        advancedFilters={hasAdvancedFilters}
                        operators={operators}
                        onChangeOperator={onChangeOperator}
                        allowedOperators={allowedOperators}
                        {...props}
                    />
                )}
            </Fragment>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(NewEntityFilters);
